import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'

import { ChildProps } from 'react-apollo'
import { InputProps } from './companyDataMutation'

import get from 'lodash/get'
import isNil from 'lodash/isNil'
import assignWith from 'lodash/assignWith'
import defaultTo from 'lodash/defaultTo'
import pick from 'lodash/pick'

import PageLayout from 'components/layout/pageLayout'
import Head from 'components/layout/head'
import Button from 'components/ui/button'
import SubNavigation from 'components/layout/subNavigation'
import Input from 'components/ui/input'

interface SettingsUserDataProps extends ChildProps<InputProps, Response>, RouteComponentProps {
  userData: any
  companyData: any
}

interface IErrorObject {
  [k: string]: string | null
}

type SettingsUserDataState = {
  errors: IErrorObject | null
  success: boolean
  company: string | null
  contact: string | null
  street: string | null
  phone: string | null
  zipCode: string | null
  email: string | null
  city: string | null
  fax: string | null
  webAdress: string | null
}

class SettingsUserData extends Component<SettingsUserDataProps, SettingsUserDataState> {
  constructor(props: SettingsUserDataProps) {
    super(props)
    this.state = {
      errors: null,
      success: false,
      ...this.formFields,
    }
  }

  formFields = {
    company: null,
    contact: null,
    street: null,
    phone: null,
    zipCode: null,
    email: null,
    city: null,
    fax: null,
    webAdress: null,
  }

  getVariables = () => {
    const { getCompanyData: companyData } = this.props.companyData
    const formFields = Object.keys(this.formFields)
    const variables = assignWith(companyData, this.state, (objValue: any, srcValue: any) =>
      isNil(srcValue) ? objValue : srcValue,
    )

    // Filter out non form fields from state object
    return pick(variables, formFields)
  }

  handleSave = () => {
    const { mutate } = this.props

    const errors = {}

    const hasErrors = Object.values(errors).find((error) => {
      if (error) return true
      return false
    })

    if (hasErrors) {
      this.setState({ errors, success: false })
    } else {
      if (mutate) {
        mutate({
          variables: { ...this.getVariables() },
        })
          .then((res) => {
            // Company data change was sucessfull
            if (get(res, 'data.setCompanyData')) {
              this.setState({ errors: null, success: true })
            } else {
              this.setState({
                errors: {
                  api: 'Error Message',
                },
                success: false,
              })
            }
          })
          .catch((err) => {
            this.setState({
              errors: {
                api: 'Error Message',
              },
              success: false,
            })
          })
      }
    }
  }

  getValue = (key: string) => {
    const { getCompanyData: companyData } = this.props.companyData
    const stateValue = get(this.state, key)

    if (!isNil(stateValue)) {
      return stateValue
    }

    return get(companyData, key)
  }

  isEmpty = (value: string) => isNil(value) || value === ''

  fillWithUserData = () => {
    const { getUserData: userData } = this.props.userData

    this.setState({
      ...userData,
      contact: `${userData.firstname} ${userData.lastname}`,
    })
  }

  render() {
    const { errors, success } = this.state

    return (
      <PageLayout pageTitle="Einstellungen">
        <Head
          headline="Einstellungen"
          subheadline={
            process.env.REACT_APP_ENV === 'rigips'
              ? 'Mengenermittlung und Systemsuche - HORST'
              : 'ABK calcKit – der Baukasten für die schnelle Kalkulation'
          }
          error={errors ? true : false}
          message={
            errors ? 'Pflichtfelder ausfüllen!' : success ? 'Änderungen gespeichert!' : undefined
          }
          actions={
            <Button primary onClick={this.handleSave}>
              Speichern
            </Button>
          }
        />
        <SubNavigation
          items={[
            {
              label: 'Nutzerdaten',
              link: `/settings/user-data`,
            },
            {
              link: `/settings/logo`,
              label: 'Firmenlogo',
            },
            {
              link: `/settings/company-details`,
              label: 'Firmendaten',
            },
            {
              link: `/settings/reset-password`,
              label: 'Passwort ändern',
            },
            {
              link: `/settings/price-overview`,
              label: 'Preisübersicht',
            },
            {
              link: `/settings/time-overview`,
              label: 'Zeitenübersicht',
            },
          ]}
        />
        <div className="px-48 mb-65">
          <div className="flex">
            <div className="w-4/12 flex items-end">
              <Input
                name="company"
                label="Büro/Firma"
                value={defaultTo(this.getValue('company'), '')}
                leftAlign
                error={errors && errors.company ? errors.company : undefined}
                onChange={(value) => this.setState({ company: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="contact"
                label="Kontakt"
                value={defaultTo(this.getValue('contact'), '')}
                leftAlign
                error={errors && errors.contact ? errors.contact : undefined}
                onChange={(value) => this.setState({ contact: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end justify-end">
              <Button secondary onClick={this.fillWithUserData}>
                Mit Nutzerdaten füllen
              </Button>
            </div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="street"
                label="Straße"
                value={defaultTo(this.getValue('street'), '')}
                leftAlign
                error={errors && errors.street ? errors.street : undefined}
                onChange={(value) => this.setState({ street: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="zipCode"
                min={0}
                label="PLZ"
                leftAlign
                value={defaultTo(this.getValue('zipCode'), '')}
                error={errors && errors.zipCode ? errors.zipCode : undefined}
                onChange={(value) => this.setState({ zipCode: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="city"
                label="Ort"
                leftAlign
                value={defaultTo(this.getValue('city'), '')}
                error={errors && errors.city ? errors.city : undefined}
                onChange={(value) => this.setState({ city: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="phone"
                label="Telefon"
                value={defaultTo(this.getValue('phone'), '')}
                leftAlign
                error={errors && errors.phone ? errors.phone : undefined}
                onChange={(value) => this.setState({ phone: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="email"
                type="email"
                label="E-Mail"
                value={defaultTo(this.getValue('email'), '')}
                leftAlign
                error={errors && errors.email ? errors.email : undefined}
                onChange={(value) => this.setState({ email: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="webAdress"
                label="Website"
                leftAlign
                value={defaultTo(this.getValue('webAdress'), '')}
                onChange={(value) => this.setState({ webAdress: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="fax"
                label="Fax"
                value={defaultTo(this.getValue('fax'), '')}
                leftAlign
                onChange={(value) => this.setState({ fax: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>
        </div>
      </PageLayout>
    )
  }
}

export default SettingsUserData
